import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Diálogo para agregar o cambiar un puesto: un solo campo y un botón "Guardar puesto" visible.
 * Es un formulario, así que Enter en el campo guarda con el mismo envío que el botón. No se
 * cierra con un clic fuera ni con Escape si hay algo escrito (`sucio`), ni mientras guarda.
 *
 * @param {object} props
 * @param {string} props.idTitulo Id del título, para `aria-labelledby`.
 * @param {string} props.titulo
 * @param {string} props.valor Texto del campo.
 * @param {(valor: string) => void} props.onCambio
 * @param {string} [props.error] Texto de error junto al campo.
 * @param {boolean} props.guardando
 * @param {boolean} props.sucio Hay cambios sin guardar.
 * @param {() => void} props.onGuardar
 * @param {() => void} props.onCerrar
 */
const DialogoPuesto = ({
  idTitulo,
  titulo,
  valor,
  onCambio,
  error,
  guardando,
  sucio,
  onGuardar,
  onCerrar,
}) => {
  const cerrar = (_evento, razon) => {
    if (guardando) return;
    if ((razon === "backdropClick" || razon === "escapeKeyDown") && sucio) return;
    onCerrar();
  };

  return (
    <Dialog
      open
      onClose={cerrar}
      fullWidth
      maxWidth="sm"
      aria-labelledby={idTitulo}
      PaperProps={{
        component: "form",
        noValidate: true,
        onSubmit: (evento) => {
          evento.preventDefault();
          onGuardar();
        },
      }}
      sx={{
        "& .MuiDialog-paper": {
          m: { xs: 1.5, sm: 4 },
          maxWidth: { xs: "calc(100% - 24px)", sm: 520 },
        },
      }}
    >
      <DialogTitle
        id={idTitulo}
        component="h2"
        sx={{ fontWeight: 700, color: "primary.main", pr: 7 }}
      >
        {titulo}
      </DialogTitle>
      <IconButton
        aria-label="Cerrar"
        onClick={() => cerrar()}
        disabled={guardando}
        sx={{
          position: "absolute",
          top: (theme) => theme.spacing(1.5),
          right: (theme) => theme.spacing(1.5),
          color: "text.secondary",
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <TextField
          required
          fullWidth
          autoFocus
          label="Nombre del puesto"
          value={valor}
          onChange={(e) => onCambio(e.target.value.toUpperCase())}
          error={Boolean(error)}
          helperText={error || "Por ejemplo: GESTOR DE CRÉDITO. Se guarda en mayúsculas."}
          disabled={guardando}
          inputProps={{ autoComplete: "off" }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => cerrar()} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={guardando}
          startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {guardando ? "Guardando…" : "Guardar puesto"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogoPuesto;
